/**
 * Service API pour le chat Adha
 * 
 * @description Gère la communication avec le backend IA pour le chat assistant
 * @since 2025-11-05
 */

import { SendMessageParams, ChatApiResponse } from '../types/chat';

// Configuration par défaut
const DEFAULT_CONFIG = {
  apiEndpoint: '/api/chat', // À configurer selon votre backend
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
};

class ChatApiService {
  private baseUrl: string;
  private timeout: number;
  private retryAttempts: number;
  private retryDelay: number;

  constructor() {
    // Utiliser la même base URL que le reste de l'application
    this.baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1';
    this.timeout = DEFAULT_CONFIG.timeout;
    this.retryAttempts = DEFAULT_CONFIG.retryAttempts;
    this.retryDelay = DEFAULT_CONFIG.retryDelay;
  }

  /**
   * Vérifie la connexion avec le backend
   */
  async checkConnection(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      clearTimeout(timeoutId);
      return response.ok;
    } catch (error) {
      console.warn('Connexion chat indisponible:', error);
      return false;
    }
  }

  /**
   * Envoie un message à l'IA avec l'historique complet de la conversation
   */
  async sendMessage(
    params: SendMessageParams, 
    signal?: AbortSignal
  ): Promise<ChatApiResponse> {
    const { content, conversationHistory = [], context = {}, userId, sessionId, onStream } = params;

    // Validation des paramètres
    if (!content?.trim()) {
      throw new Error('Le message ne peut pas être vide');
    }

    const payload = {
      content: content.trim(),
      conversationHistory: conversationHistory.map(msg => ({
        role: msg.isBot ? 'assistant' : 'user',
        content: msg.content,
        timestamp: msg.timestamp.toISOString(),
      })),
      context: {
        ...context,
        userId,
        sessionId,
        timestamp: new Date().toISOString(),
        platform: 'web',
        userAgent: navigator.userAgent,
      },
    };

    // Si un callback de streaming est fourni, utiliser la méthode streaming
    if (onStream) {
      return this.sendMessageStreaming(payload, signal, onStream);
    }

    return this.makeRequest('/chat/message', 'POST', payload, signal);
  }

  /**
   * Envoie un message avec support du streaming SSE (Server-Sent Events)
   */
  private async sendMessageStreaming(
    payload: any,
    signal?: AbortSignal,
    onStream?: (chunk: string) => void
  ): Promise<ChatApiResponse> {
    const url = `${this.baseUrl}/chat/message`;
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      // Combiner les signaux d'annulation
      const combinedSignal = signal 
        ? this.combineAbortSignals(signal, controller.signal)
        : controller.signal;

      const response = await fetch(url, {
        method: 'POST',
        signal: combinedSignal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...this.getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Vérifier si la réponse est en streaming
      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('text/event-stream')) {
        // Si ce n'est pas du streaming, traiter comme JSON normal
        const result = await response.json();
        return {
          success: true,
          message: result.content || result.message || result.response,
          conversationId: result.conversationId,
          timestamp: result.timestamp || result.createdAt,
          metadata: {
            ...result.metadata,
            messageId: result.id,
            role: result.role,
            status: result.status,
          },
        };
      }

      // Gérer le streaming SSE
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Streaming non supporté');
      }

      const decoder = new TextDecoder();
      let accumulatedMessage = '';
      let conversationId = '';
      let messageMetadata: any = {};

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (!line.trim() || line.startsWith(':')) continue;

            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              
              if (data === '[DONE]') {
                continue;
              }

              try {
                const parsed = JSON.parse(data);
                
                // Extraire les métadonnées
                if (parsed.conversationId) {
                  conversationId = parsed.conversationId;
                }
                if (parsed.id) {
                  messageMetadata.messageId = parsed.id;
                }
                if (parsed.role) {
                  messageMetadata.role = parsed.role;
                }
                if (parsed.status) {
                  messageMetadata.status = parsed.status;
                }
                if (parsed.createdAt) {
                  messageMetadata.createdAt = parsed.createdAt;
                }

                // Accumuler le contenu
                if (parsed.content) {
                  accumulatedMessage += parsed.content;
                  
                  // Appeler le callback de streaming
                  if (onStream) {
                    onStream(parsed.content);
                  }
                } else if (parsed.delta) {
                  // Support pour différents formats de streaming
                  accumulatedMessage += parsed.delta;
                  if (onStream) {
                    onStream(parsed.delta);
                  }
                }
              } catch (parseError) {
                console.warn('Erreur parsing SSE chunk:', parseError);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      return {
        success: true,
        message: accumulatedMessage,
        conversationId,
        timestamp: messageMetadata.createdAt || new Date().toISOString(),
        metadata: messageMetadata,
      };

    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw error;
      }

      console.error('Erreur streaming chat:', error);
      
      return {
        success: false,
        error: error.message || 'Erreur de communication avec le serveur',
      };
    }
  }

  /**
   * Récupère l'historique d'une conversation
   */
  async getConversationHistory(conversationId: string, signal?: AbortSignal): Promise<ChatApiResponse> {
    return this.makeRequest(`/chat/conversations/${conversationId}`, 'GET', null, signal);
  }

  /**
   * Sauvegarde une conversation
   */
  async saveConversation(
    conversationData: any, 
    signal?: AbortSignal
  ): Promise<ChatApiResponse> {
    return this.makeRequest('/chat/conversations', 'POST', conversationData, signal);
  }

  /**
   * Supprime une conversation
   */
  async deleteConversation(conversationId: string, signal?: AbortSignal): Promise<ChatApiResponse> {
    return this.makeRequest(`/chat/conversations/${conversationId}`, 'DELETE', null, signal);
  }

  /**
   * Méthode privée pour effectuer les requêtes HTTP avec retry
   */
  private async makeRequest(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    data?: any,
    signal?: AbortSignal,
    attempt: number = 1
  ): Promise<ChatApiResponse> {
    const url = `${this.baseUrl}${endpoint}`;
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      // Combiner les signaux d'annulation
      const combinedSignal = signal 
        ? this.combineAbortSignals(signal, controller.signal)
        : controller.signal;

      const options: RequestInit = {
        method,
        signal: combinedSignal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...this.getAuthHeaders(),
        },
      };

      if (data && method !== 'GET') {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(url, options);
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        message: result.content || result.message || result.response,
        conversationId: result.conversationId,
        timestamp: result.timestamp || result.createdAt,
        metadata: {
          ...result.metadata,
          messageId: result.id,
          role: result.role,
          status: result.status,
        },
      };

    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw error; // Propagation des annulations
      }

      console.error(`Erreur API chat (tentative ${attempt}/${this.retryAttempts}):`, error);

      // Retry logic pour les erreurs réseau
      if (attempt < this.retryAttempts && this.isRetryableError(error)) {
        await this.delay(this.retryDelay * attempt);
        return this.makeRequest(endpoint, method, data, signal, attempt + 1);
      }

      // Mode dégradé avec réponses simulées
      if (endpoint.includes('/chat/message')) {
        console.warn('Mode dégradé activé pour le chat');
        return this.getFallbackResponse(data?.content || '');
      }

      return {
        success: false,
        error: error.message || 'Erreur de communication avec le serveur',
      };
    }
  }

  /**
   * Récupère les headers d'authentification
   */
  private getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    
    // Token JWT depuis le localStorage ou context Auth0
    const token = localStorage.getItem('wanzo_auth_token') || 
                  localStorage.getItem('auth0_token');
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Combine plusieurs AbortSignal
   */
  private combineAbortSignals(...signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();
    
    for (const signal of signals) {
      if (signal.aborted) {
        controller.abort();
        break;
      }
      signal.addEventListener('abort', () => controller.abort());
    }
    
    return controller.signal;
  }

  /**
   * Détermine si une erreur est retriable
   */
  private isRetryableError(error: any): boolean {
    // Erreurs réseau temporaires
    return error.message?.includes('fetch') ||
           error.message?.includes('network') ||
           error.message?.includes('timeout') ||
           error.name === 'NetworkError';
  }

  /**
   * Délai pour les tentatives de retry
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Réponse de fallback en mode dégradé
   */
  private getFallbackResponse(userMessage: string): ChatApiResponse {
    const fallbackResponses = [
      "Je suis temporairement indisponible. Veuillez réessayer dans quelques instants.",
      "Connexion au serveur interrompue. Vos messages sont sauvegardés et je vous répondrai dès que possible.",
      "Service temporairement indisponible. L'équipe technique travaille à résoudre le problème.",
    ];

    // Réponses contextuelles basiques
    const lowerMessage = userMessage.toLowerCase();
    let response = fallbackResponses[0];

    if (lowerMessage.includes('aide') || lowerMessage.includes('help')) {
      response = "Je ne peux pas vous aider actuellement car je suis hors ligne. Essayez de recharger la page ou contactez le support technique.";
    } else if (lowerMessage.includes('merci') || lowerMessage.includes('thank')) {
      response = "De rien ! Bien que je sois temporairement indisponible, votre message a été enregistré.";
    }

    return {
      success: true,
      message: response,
      conversationId: `fallback_${Date.now()}`,
      timestamp: new Date().toISOString(),
      metadata: {
        fallback: true,
        reason: 'service_unavailable',
      },
    };
  }
}

// Instance singleton
export const chatApiService = new ChatApiService();